import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Text } from '@companycam/slab-web';

import { translate } from 'translations/i18n';

type BrandColorPickerProps = {
  brandColor: string;
  onColorPicked: (color: string) => void;
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ColorPickerContainer = styled.div`
  position: relative;
  padding: var(--cc_size_spacing_xs);
  border: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
  border-radius: var(--cc_size_border_radius_m);
  width: 56px;
  height: 32px;
`;

const ColorPickerInput = styled.input`
  width: 100%;
  height: 100%;
  opacity: 0;
  position: absolute;
  cursor: pointer;
`;

const ColorSwatch = styled.button`
  width: 100%;
  height: 100%;
  padding: var(--cc_size_spacing_xs);
  border-radius: var(--cc_size_border_radius_s);
  background-color: ${(props) => props.color};
  border: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
  cursor: pointer;
  position: relative;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const TextContainer = styled.div``;

const BrandColorPicker = ({
  brandColor,
  onColorPicked,
}: BrandColorPickerProps) => {
  const [color, setColor] = useState(`#${brandColor}`);

  useEffect(() => {
    const colorPicker = document.getElementById('brand-color-picker');

    const handleColorPickerChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const updatedColor = target.value;
      onColorPicked(updatedColor);
    };

    // using "change" event listener to only update when color picker is closed
    if (colorPicker) {
      colorPicker.addEventListener('change', handleColorPickerChange, false);
    }

    return () => {
      if (colorPicker) {
        colorPicker.removeEventListener(
          'change',
          handleColorPickerChange,
          false,
        );
      }
    };
  }, [onColorPicked]);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColor(e.target.value);
  };

  return (
    <Container>
      <TextContainer>
        <Text as="h3" size="m" weight={700} family="heading">
          {translate('mapEmbedWizard.pinColorTitle')}
        </Text>
        <Text as="p" size="xs" family="heading">
          {translate('mapEmbedWizard.pinColorDescription')}
        </Text>
      </TextContainer>
      <ColorPickerContainer>
        {/* hidden from UI, functionality only */}
        <ColorPickerInput
          id="brand-color-picker"
          type="color"
          onChange={handleColorChange}
          value={color}
        />
        {/* helps with custom style of the color being displayed */}
        <ColorSwatch
          color={color}
          onClick={() => document.getElementById('brand-color-picker')?.click()}
        />
      </ColorPickerContainer>
    </Container>
  );
};

export default BrandColorPicker;
